import React, { useState, useEffect } from 'react'
import { useParams, useHistory } from 'react-router'

// Reusing your existing CSS file for layout consistency
import '../../PlaydynamexNew/PlaydynamexNew.css'
import { servo_motor } from '../../concept/ComponentsSelected'
import { FaRegSave } from 'react-icons/fa'
import { AiOutlineAudio } from 'react-icons/ai'
import { confirmVoiceoverGeneration } from '../voiceoverCredits'

import PORT_MAP from '../../AssemblyTwo/AllowedPortTypes'
// Curated per-board component catalog — the SAME source the live assembly uses
// (assemblyAccessoriesApi -> getDeviceComponents). Keyed by board (TOKI1, POKI1...).
import COMPONENT_CATALOG from '../../concept/data'

const BACKEND_URL = process.env.REACT_APP_API_BASE
const STAGING_URL = `${BACKEND_URL}/api/staging`

// --- BOARD-AWARE CONFIGURATION ---
// The admin panel supports the mini "-ki" assembly boards (Toki, Poki, Moki, ...).
// All hardware-specific data is keyed by the board key used in AllowedPortTypes
// (e.g. TOKI1 / POKI1), following the same `Device + version` convention used by
// the live assembly (see CustomDragLayer.generateAllowPortTypeKey).

const DEFAULT_BOARD_KEY = 'TOKI1'

// Display labels: internal port keys -> physical labels printed on the board.
// Shared across the -ki boards, which use the same motor/servo silkscreen labels.
// Any port not listed here (A1, B1, E, F1, ...) is shown using its own key.
const PORT_NAME_MAP = {
    servo_motor1: 'S1',
    servo_motor2: 'S2',
    M3: 'M2',
}

// Per-board configuration.
//  - internalAccessories: checkboxes shown under "Internal Accessories".
//  - excludedAccessories: FALLBACK ONLY. The external-accessory list is normally
//    derived from the component catalog (concept/data). This list is used only when
//    a board has no catalog entry, to hide components that live on fixed internal
//    ports from the port-map-derived fallback list.
const BOARD_CONFIG = {
    TOKI1: {
        internalAccessories: ['ir sensor'],
        excludedAccessories: [
            'Magnetometer',
            'Magnetic_encoder',
            'IMU',
            'OLED',
            'Color_sensor',
        ],
    },
    POKI1: {
        internalAccessories: ['ir sensor'],
        excludedAccessories: [
            'Magnetometer',
            'Magnetic_encoder',
            'IMU',
            'OLED',
            'Color_sensor',
            'Audio_codec',
        ],
    },
}

// Resolve the AllowedPortTypes board key from the stored device name.
// Mirrors the `Device + version` convention (Toki v1 -> TOKI1, Poki v1 -> POKI1).
// Falls back to TOKI1 if the device is unknown or has no port map entry.
const resolveBoardKey = (device) => {
    const raw = (device || 'Toki').toString().trim()
    const match = raw.match(/^([A-Za-z]+)(\d*)$/)
    const name = (match ? match[1] : raw).toUpperCase()
    const version = (match && match[2]) || '1'

    if (PORT_MAP[`${name}${version}`]) return `${name}${version}`
    if (PORT_MAP[`${name}1`]) return `${name}1`
    return DEFAULT_BOARD_KEY
}

const AssemblyAdmin = () => {
    const { subId, screenId } = useParams()
    const history = useHistory()
    const device = sessionStorage.getItem('allowedDevice') || 'Toki'

    // Resolve all board-specific data from the active device so the admin
    // surfaces the correct ports / components for TOKI, POKI, etc.
    const boardKey = resolveBoardKey(device)
    const boardPortMap = PORT_MAP[boardKey] || PORT_MAP[DEFAULT_BOARD_KEY]
    const boardConfig = BOARD_CONFIG[boardKey] || BOARD_CONFIG[DEFAULT_BOARD_KEY]
    const internalAccessoriesList = boardConfig.internalAccessories

    // External accessories are sourced from the curated per-board component catalog
    // (concept/data) — the exact set the live assembly offers for this board — rather
    // than from raw port-map keys. This is what makes POKI surface its real components
    // (OLED, IMU, Color sensor, ...) while TOKI keeps its own narrower set.
    // We keep only catalog entries that also have a port mapping for this board, and
    // fall back to the port map (minus internal-only components) if a board has no
    // catalog entry yet.
    const catalogComponents = COMPONENT_CATALOG[boardKey] || []
    const boardComponents = catalogComponents.length
        ? catalogComponents.filter((comp) => boardPortMap[comp.type])
        : Object.keys(boardPortMap)
              .filter((type) => !boardConfig.excludedAccessories.includes(type))
              .map((type) => ({ type }))

    // STATE
    const [loading, setLoading] = useState(true)
    const [formData, setFormData] = useState(null)
    const [activeStepIndex, setActiveStepIndex] = useState(0)
    const [subprogramTitle, setSubprogramTitle] = useState('')

    // NEW STATE: TTS Generation tracking
    const [isGeneratingTTS, setIsGeneratingTTS] = useState(false)

    // Build the API path: include screenId when available
    const apiPath = screenId
        ? `assembly/${subId}/${screenId}`
        : `assembly/${subId}`

    // 1. FETCH DATA
    useEffect(() => {
        const fetchProject = async () => {
            try {
                const res = await fetch(`${STAGING_URL}/${apiPath}`, {
                    credentials: 'include',
                })
                if (!res.ok) throw new Error('Failed to fetch staging data')
                const data = await res.json()

                setFormData({
                    title: data.title || '',
                    totalSteps: data.totalSteps || 0,
                    steps: data.steps || [],
                    internalAccessories: data.internalAccessories || [],
                    externalAccessories: data.externalAccessories || [],
                    // NEW: Add final step fields
                    finalStepTitle: data.finalStepTitle || 'All Components assembled!',
                    finalStepInstruction:
                        data.finalStepInstruction ||
                        'Congratulations on completing your electronic connections! Click Continue to proceed.',
                    // NEW: Controls whether the Next button is usable in the player.
                    // Default false (Next disabled) so tutorials created before this
                    // feature keep the button off until an admin explicitly enables it.
                    nextAllowed:
                        data.nextAllowed === undefined ? false : data.nextAllowed,
                })

                setSubprogramTitle(await fetchSubprogramTitle())
                setLoading(false)
            } catch (error) {
                console.error('Error fetching admin data:', error)
                setLoading(false)
            }
        }
        fetchProject()
    }, [subId, screenId, apiPath])

    const fetchSubprogramTitle = async () => {
        const BACKEND_URL = process.env.REACT_APP_API_BASE || ''

        if (!subId) return ''

        const res = await fetch(`${BACKEND_URL}/api/program/staging/${device.toLowerCase()}`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
        })

        const data = await res.json()
        if (!res.ok) {
            throw new Error(data.error || 'Failed to get program data')
        }

        const matchedSubprogram = (data.subprograms || []).find(
            (s) => s.id === subId
        )

        return matchedSubprogram?.title || ''
    }

    // 2. HANDLE GENERAL CHANGES
    const handleTitleChange = (e) => {
        setFormData((prev) => ({ ...prev, title: e.target.value }))
        // Do nothing for now. Later it will be used for the tutorial (for users on top side)
    }

    const handleTotalStepsChange = (e) => {
        const newTotal = parseInt(e.target.value) || 0
        setFormData((prev) => {
            const newSteps = [...prev.steps]

            // Add new empty steps if increased
            if (newTotal > newSteps.length) {
                for (let i = newSteps.length + 1; i <= newTotal; i++) {
                    newSteps.push({
                        stepNumber: i,
                        title: '',
                        instruction: '',
                        component: '',
                        port: '',
                    })
                }
            }
            // Truncate if decreased
            else if (newTotal < newSteps.length) {
                newSteps.length = newTotal
            }

            return { ...prev, totalSteps: newTotal, steps: newSteps }
        })

        // Prevent active index from exceeding new total
        setActiveStepIndex((prev) => Math.min(prev, Math.max(0, newTotal - 1)))
    }

    // Handle Final Step Changes
    const handleFinalStepChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }))
    }

    // 3. HANDLE CHECKBOXES (FIXED)
    const handleCheckboxChange = (type, item, isChecked) => {
        setFormData((prev) => {
            const list = new Set(prev[type])
            if (isChecked) list.add(item)
            else list.delete(item)

            const newList = Array.from(list)
            let updatedSteps = prev.steps

            // FIX: If a component is unchecked, remove it from any steps that were using it
            if (type === 'externalAccessories' && !isChecked) {
                updatedSteps = prev.steps.map((step) => {
                    if (step.component === item) {
                        return { ...step, component: '', port: '' }
                    }
                    return step
                })
            }

            return { ...prev, [type]: newList, steps: updatedSteps }
        })
    }

    // 4. HANDLE STEP DATA CHANGES
    const handleStepChange = (index, field, value) => {
        setFormData((prev) => {
            const updatedSteps = [...prev.steps]
            const currentStep = { ...updatedSteps[index] }

            currentStep[field] = value

            // If they changed the component, auto-clear the port if it's no longer valid
            if (field === 'component') {
                const allowedPorts =
                    boardPortMap[value] || boardPortMap['DEFAULT'] || []
                if (!allowedPorts.includes(currentStep.port)) {
                    currentStep.port = '' // Clear invalid port
                }
            }

            updatedSteps[index] = currentStep
            return { ...prev, steps: updatedSteps }
        })
    }

    // 5. SAVE
    const handleSave = async () => {
        try {
            const res = await fetch(`${STAGING_URL}/${apiPath}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData),
                credentials: 'include',
            })

            if (!res.ok) throw new Error('Failed to save data')
            alert('Electronic tutorial saved successfully to staging!')
        } catch (error) {
            console.error('Save error:', error)
            alert('Failed to save tutorial.')
        }
    }

    // 6. GENERATE VOICEOVER (NEW)
    const handleGenerateVoiceover = async () => {
        // Remind them to save first, because the backend reads from the JSON file
        // Single confirmation gate: save reminder + estimated credit cost.
        if (!confirmVoiceoverGeneration(formData)) return

        // Fallback to 'assembly1' if dealing with older legacy routes
        const currentScreenId = screenId || 'assembly1'

        setIsGeneratingTTS(true)
        try {
            // --- READ FROM LOCAL STORAGE ---
            const apiKey = localStorage.getItem('freepikApiKey')
            const voiceId = localStorage.getItem('elevenVoiceId')
            if (!apiKey || !voiceId) {
                window.alert(
                    `Missing TTS Configuration!\n\nPlease go back to the main ${device} Engine Admin page and set the ElevenLabs API Key and Voice ID before generating voiceovers.`
                )
                setIsGeneratingTTS(false)
                return
            }

            const res = await fetch(
                `${BACKEND_URL}/api/project/${subId}/generate-tts/assembly/${currentScreenId}`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' }, // IMPORTANT: Add this header
                    body: JSON.stringify({ apiKey, voiceId }), // IMPORTANT: Send the keys
                }
            )
            const data = await res.json()

            if (!res.ok) {
                throw new Error(data.error || 'Failed to generate voiceover')
            }

            alert(`✅ ${data.message}`)
        } catch (error) {
            console.error('TTS Generation error:', error)
            alert(`❌ Error generating voiceover: ${error.message}`)
        } finally {
            setIsGeneratingTTS(false)
        }
    }

    const handlePreviewClick = () => {
        if (
            !window.confirm(
                'Have you saved all your changes? Unsaved changes will be lost. Click OK to continue and preview the last saved version.'
            )
        )
            return
        const previewUrl = `/assemblyTwo?preview=true&projectId=${subId}${screenId ? `&screenId=${screenId}` : ''}`
        history.push(previewUrl)
    }

    // ==========================================
    // RENDER
    // ==========================================
    if (loading)
        return <div style={{ padding: '20px' }}>Loading Admin Panel...</div>
    if (!formData)
        return (
            <div style={{ padding: '20px' }}>
                Error loading data. Try logging in again.
            </div>
        )

    const activeStepData = formData.steps[activeStepIndex]

    // Get allowed ports for the currently selected component in this step
    const allowedPortsForActiveStep = activeStepData?.component
        ? boardPortMap[activeStepData.component] ||
          boardPortMap['DEFAULT'] ||
          []
        : []

    return (
        <div
            style={{
                padding: '20px 40px',
                maxWidth: '1000px',
                margin: '0 auto',
                fontFamily: 'sans-serif',
                height: '100vh',
                boxSizing: 'border-box',
                overflowY: 'auto',
            }}
        >
            {/* Subprogram heading row */}
            <div
                style={{
                    marginBottom: '12px',
                    padding: '10px 14px',
                    background:
                        'linear-gradient(90deg, #eef4ff 0%, #f8fbff 100%)',
                    border: '1px solid #dbe7ff',
                    borderRadius: '10px',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <div
                        style={{
                            fontSize: '0.9rem',
                            fontWeight: 700,
                            color: '#5b6b8c',
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                        }}
                    >
                        Current Project
                    </div>

                    <h1
                        style={{
                            margin: 0,
                            fontSize: '1.65rem',
                            fontWeight: 800,
                            color: '#1f3b77',
                            lineHeight: 1.2,
                        }}
                    >
                        {subprogramTitle}
                    </h1>
                </div>
            </div>

            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '20px',
                }}
            >
                <h1 style={{ margin: 0, fontSize: '1.8rem' }}>
                    Electronic Editor
                </h1>
                <div style={{ display: 'flex', gap: '10px' }}>
                    {/* NEW: Generate Voiceover Button */}
                    <button
                        onClick={handleGenerateVoiceover}
                        disabled={isGeneratingTTS}
                        style={{
                            padding: '10px 20px',
                            background: isGeneratingTTS ? '#9e9e9e' : '#ff9800', // Orange color for audio tools
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: isGeneratingTTS ? 'not-allowed' : 'pointer',
                            fontWeight: 'bold',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px',
                        }}
                    >
                        {isGeneratingTTS ? (
                            'Generating...'
                        ) : (
                            <>
                                <AiOutlineAudio
                                    style={{
                                        fontSize: '22px',
                                        marginRight: '12px',
                                    }}
                                />{' '}
                                Generate Voiceover
                            </>
                        )}
                    </button>

                    <button
                        onClick={handlePreviewClick}
                        style={{
                            padding: '10px 20px',
                            background: '#6200ea',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontWeight: 'bold',
                        }}
                    >
                        Preview
                    </button>
                    <button
                        onClick={handleSave}
                        style={{
                            padding: '10px 20px',
                            background: '#1a73e8',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontWeight: 'bold',
                        }}
                    >
                        <FaRegSave
                            style={{
                                fontSize: '22px',
                                marginRight: '12px',
                            }}
                        />
                        Save
                    </button>
                </div>
            </div>

            {/* General Settings & Accessories */}
            <div
                style={{
                    background: '#f4f4f9',
                    padding: '20px',
                    borderRadius: '8px',
                    marginBottom: '20px',
                    border: '1px solid #e0e0e0',
                }}
            >
                <div style={{ marginBottom: '20px' }}>
                    <label
                        style={{
                            display: 'block',
                            fontWeight: 'bold',
                            marginBottom: '5px',
                        }}
                    >
                        Tutorial Title
                    </label>
                    <input
                        type="text"
                        value={formData.title}
                        onChange={handleTitleChange}
                        style={{
                            width: '100%',
                            padding: '10px',
                            borderRadius: '4px',
                            border: '1px solid #ccc',
                            boxSizing: 'border-box',
                        }}
                    />
                </div>

                {/* NEW: Lock the Next button so users must complete each
                    connection (by dragging) instead of skipping ahead. */}
                <div style={{ marginBottom: '20px' }}>
                    <label
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            cursor: 'pointer',
                            fontWeight: 'bold',
                        }}
                    >
                        <input
                            type="checkbox"
                            checked={formData.nextAllowed === false}
                            onChange={(e) =>
                                handleFinalStepChange(
                                    'nextAllowed',
                                    !e.target.checked
                                )
                            }
                        />
                        Disable the "Next" button
                    </label>
                    <p
                        style={{
                            margin: '6px 0 0 28px',
                            fontSize: '0.85rem',
                            color: '#666',
                        }}
                    >
                        When enabled, users cannot skip ahead with the Next
                        button and must complete each connection to advance.
                    </p>
                </div>

                <div style={{ display: 'flex', gap: '40px' }}>
                    {/* Internal Accessories */}
                    <div style={{ flex: 1 }}>
                        <label
                            style={{
                                display: 'block',
                                fontWeight: 'bold',
                                marginBottom: '10px',
                            }}
                        >
                            Internal Accessories
                        </label>
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr',
                                gap: '8px',
                                background: '#fff',
                                padding: '15px',
                                borderRadius: '6px',
                                border: '1px solid #ccc',
                            }}
                        >
                            {internalAccessoriesList.map((acc) => (
                                <label
                                    key={acc}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        cursor: 'pointer',
                                        textTransform: 'capitalize',
                                        fontSize: '0.9rem',
                                    }}
                                >
                                    <input
                                        type="checkbox"
                                        checked={formData.internalAccessories.includes(
                                            acc
                                        )}
                                        onChange={(e) =>
                                            handleCheckboxChange(
                                                'internalAccessories',
                                                acc,
                                                e.target.checked
                                            )
                                        }
                                    />
                                    {acc}
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* External Accessories */}
                    <div style={{ flex: 1 }}>
                        <label
                            style={{
                                display: 'block',
                                fontWeight: 'bold',
                                marginBottom: '10px',
                            }}
                        >
                            External Accessories
                        </label>
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr',
                                gap: '8px',
                                background: '#fff',
                                padding: '15px',
                                borderRadius: '6px',
                                border: '1px solid #ccc',
                                maxHeight: '180px',
                                overflowY: 'auto',
                            }}
                        >
                            {boardComponents.map((comp) => (
                                <label
                                    key={comp.type}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        cursor: 'pointer',
                                        fontSize: '0.9rem',
                                    }}
                                >
                                    <input
                                        type="checkbox"
                                        checked={formData.externalAccessories.includes(
                                            comp.type
                                        )}
                                        onChange={(e) =>
                                            handleCheckboxChange(
                                                'externalAccessories',
                                                comp.type,
                                                e.target.checked
                                            )
                                        }
                                    />
                                    {comp.type.replace(/_/g, ' ')}
                                </label>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Steps Configuration */}
            <div>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '15px',
                        marginBottom: '15px',
                    }}
                >
                    <h2 style={{ margin: 0 }}>Electronic Steps</h2>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                        }}
                    >
                        <label style={{ fontWeight: 'bold', color: '#555' }}>
                            Total Steps:
                        </label>
                        <input
                            type="number"
                            min="0"
                            max="50"
                            value={formData.totalSteps}
                            onChange={handleTotalStepsChange}
                            style={{
                                width: '60px',
                                padding: '6px',
                                borderRadius: '4px',
                                border: '1px solid #ccc',
                                textAlign: 'center',
                            }}
                        />
                    </div>
                </div>

                {formData.totalSteps === 0 ? (
                    <p
                        style={{
                            color: '#d32f2f',
                            fontWeight: 'bold',
                            padding: '20px',
                            background: '#ffebee',
                            borderRadius: '8px',
                        }}
                    >
                        Set "Total Steps" to 1 or more to start adding
                        instructions.
                    </p>
                ) : (
                    <div
                        style={{
                            border: '1px solid #ddd',
                            padding: '20px',
                            borderRadius: '8px',
                            background: '#fff',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                        }}
                    >
                        {/* Pagination Header */}
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '20px',
                                paddingBottom: '15px',
                                borderBottom: '1px solid #eee',
                            }}
                        >
                            <h3 style={{ margin: 0, color: '#6200ea' }}>
                                Step {activeStepData?.stepNumber}{' '}
                                <span
                                    style={{
                                        color: '#888',
                                        fontSize: '1rem',
                                        fontWeight: 'normal',
                                    }}
                                >
                                    of {formData.totalSteps}
                                </span>
                            </h3>

                            <div style={{ display: 'flex', gap: '10px' }}>
                                <button
                                    onClick={() =>
                                        setActiveStepIndex((prev) =>
                                            Math.max(0, prev - 1)
                                        )
                                    }
                                    disabled={activeStepIndex === 0}
                                    style={{
                                        padding: '8px 16px',
                                        background:
                                            activeStepIndex === 0
                                                ? '#f0f0f0'
                                                : '#fff',
                                        border: '1px solid #ccc',
                                        borderRadius: '6px',
                                        cursor:
                                            activeStepIndex === 0
                                                ? 'not-allowed'
                                                : 'pointer',
                                    }}
                                >
                                    &larr; Previous
                                </button>
                                <button
                                    onClick={() =>
                                        setActiveStepIndex((prev) =>
                                            Math.min(
                                                formData.totalSteps - 1,
                                                prev + 1
                                            )
                                        )
                                    }
                                    disabled={
                                        activeStepIndex ===
                                        formData.totalSteps - 1
                                    }
                                    style={{
                                        padding: '8px 16px',
                                        background:
                                            activeStepIndex ===
                                            formData.totalSteps - 1
                                                ? '#f0f0f0'
                                                : '#fff',
                                        border: '1px solid #ccc',
                                        borderRadius: '6px',
                                        cursor:
                                            activeStepIndex ===
                                            formData.totalSteps - 1
                                                ? 'not-allowed'
                                                : 'pointer',
                                    }}
                                >
                                    Next &rarr;
                                </button>
                            </div>
                        </div>

                        {/* Hardware Connection Config */}
                        <div
                            style={{
                                display: 'flex',
                                gap: '20px',
                                marginBottom: '15px',
                                background: '#f8f9fa',
                                padding: '15px',
                                borderRadius: '6px',
                                border: '1px dashed #ccc',
                            }}
                        >
                            <div style={{ flex: 1 }}>
                                <label
                                    style={{
                                        display: 'block',
                                        fontWeight: 'bold',
                                        marginBottom: '5px',
                                        color: '#1a73e8',
                                    }}
                                >
                                    Component to connect
                                </label>
                                <select
                                    value={activeStepData?.component || ''}
                                    onChange={(e) =>
                                        handleStepChange(
                                            activeStepIndex,
                                            'component',
                                            e.target.value
                                        )
                                    }
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        borderRadius: '4px',
                                        border: '1px solid #ccc',
                                    }}
                                >
                                    <option value="" disabled>
                                        Select a component...
                                    </option>
                                    {formData.externalAccessories.length ===
                                        0 && (
                                        <option value="" disabled>
                                            -- Check External Accessories above
                                            --
                                        </option>
                                    )}
                                    {formData.externalAccessories.map(
                                        (comp) => (
                                            <option key={comp} value={comp}>
                                                {comp
                                                    .replace(/_/g, ' ')
                                                    .toUpperCase()}
                                            </option>
                                        )
                                    )}
                                </select>
                            </div>
                            <div style={{ flex: 1 }}>
                                <label
                                    style={{
                                        display: 'block',
                                        fontWeight: 'bold',
                                        marginBottom: '5px',
                                        color: '#d93025',
                                    }}
                                >
                                    Target Port
                                </label>
                                <select
                                    value={activeStepData?.port || ''}
                                    onChange={(e) =>
                                        handleStepChange(
                                            activeStepIndex,
                                            'port',
                                            e.target.value
                                        )
                                    }
                                    disabled={!activeStepData?.component}
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        borderRadius: '4px',
                                        border: '1px solid #ccc',
                                        background: !activeStepData?.component
                                            ? '#eee'
                                            : '#fff',
                                    }}
                                >
                                    <option value="" disabled>
                                        Select a port...
                                    </option>
                                    {allowedPortsForActiveStep.map((port) => (
                                        <option key={port} value={port}>
                                            {PORT_NAME_MAP[port] || port}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Text Editor Fields */}
                        <div style={{ marginBottom: '15px' }}>
                            <label
                                style={{
                                    display: 'block',
                                    fontWeight: 'bold',
                                    marginBottom: '5px',
                                }}
                            >
                                Step Title (Header)
                            </label>
                            <input
                                type="text"
                                value={activeStepData?.title || ''}
                                onChange={(e) =>
                                    handleStepChange(
                                        activeStepIndex,
                                        'title',
                                        e.target.value
                                    )
                                }
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    borderRadius: '4px',
                                    border: '1px solid #ccc',
                                    boxSizing: 'border-box',
                                }}
                                placeholder="e.g. Connect the Servo to S1"
                            />
                        </div>

                        <div>
                            <label
                                style={{
                                    display: 'block',
                                    fontWeight: 'bold',
                                    marginBottom: '5px',
                                }}
                            >
                                Instruction Text (Voiceover/Subtitle)
                            </label>
                            <textarea
                                value={activeStepData?.instruction || ''}
                                onChange={(e) =>
                                    handleStepChange(
                                        activeStepIndex,
                                        'instruction',
                                        e.target.value
                                    )
                                }
                                rows="3"
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    borderRadius: '4px',
                                    border: '1px solid #ccc',
                                    resize: 'none',
                                    boxSizing: 'border-box',
                                    fontFamily: 'inherit',
                                }}
                                placeholder="Explain the connection clearly..."
                            />
                        </div>
                    </div>
                )}
                {/* --- NEW: Final Step Configuration --- */}
                {formData.totalSteps > 0 && (
                    <div
                        style={{
                            marginTop: '20px',
                            background: '#f6f9ff',
                            padding: '20px',
                            borderRadius: '8px',
                            border: '1px solid #dbe7ff',
                        }}
                    >
                        <h2 style={{ margin: '0 0 15px 0', color: '#1f3b77' }}>
                            Final Step (Success)
                        </h2>

                        <div style={{ marginBottom: '15px' }}>
                            <label
                                style={{
                                    display: 'block',
                                    fontWeight: 'bold',
                                    marginBottom: '5px',
                                }}
                            >
                                Final Step Title (Header)
                            </label>
                            <input
                                type="text"
                                value={formData.finalStepTitle || ''}
                                onChange={(e) =>
                                    handleFinalStepChange(
                                        'finalStepTitle',
                                        e.target.value
                                    )
                                }
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    borderRadius: '4px',
                                    border: '1px solid #ccc',
                                    boxSizing: 'border-box',
                                }}
                                placeholder="e.g. All Components assembled!"
                            />
                        </div>

                        <div>
                            <label
                                style={{
                                    display: 'block',
                                    fontWeight: 'bold',
                                    marginBottom: '5px',
                                }}
                            >
                                Final Step Instruction (Voiceover/Subtitle)
                            </label>
                            <textarea
                                value={formData.finalStepInstruction || ''}
                                onChange={(e) =>
                                    handleFinalStepChange(
                                        'finalStepInstruction',
                                        e.target.value
                                    )
                                }
                                rows="3"
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    borderRadius: '4px',
                                    border: '1px solid #ccc',
                                    resize: 'none',
                                    boxSizing: 'border-box',
                                    fontFamily: 'inherit',
                                }}
                                placeholder="Explain what the user should do next..."
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

export default AssemblyAdmin
